import { format, isBefore, isSameDay } from 'date-fns';
import React, { useMemo, useRef } from 'react';
import { Calendar10, FlagFilled } from 'shared/assets/icons';
import { TimeLeft } from 'shared/components/ui/time-left';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { initialDateFormat } from 'shared/types/date-format-options';
import { Goal } from 'shared/types/goal';

import * as Styled from './goal-item-card.style';

export type DeadlineProps = {
  goal: Goal;
  // Timeline: only render the flag + deadline, and only when both the start
  // date and deadline are set — no start-date or "no dates" fallback state.
  deadlineOnly?: boolean;
};

export const Deadline: React.FC<DeadlineProps> = ({ goal, deadlineOnly }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const today = useToday();
  const user = useUser();
  const dateFormat = user?.settings?.dateFormat ?? initialDateFormat;

  const hasTimeframe = !goal.completedAt && !!goal.startDate && !!goal.deadline;

  const started = useMemo(
    () =>
      !goal.startDate ||
      isBefore(goal.startDate, today) ||
      isSameDay(goal.startDate, today),
    [goal.startDate, today],
  );

  const tooltip = useMemo(() => {
    if (!goal.startDate && !goal.deadline) {
      return;
    }

    return [
      !started && goal.startDate
        ? format(goal.startDate, dateFormat)
        : undefined,
      goal.deadline ? format(goal.deadline, dateFormat) : undefined,
    ]
      .filter(Boolean)
      .join(' - ');
  }, [dateFormat, goal.deadline, goal.startDate, started]);

  if (!hasTimeframe) {
    return null;
  }

  if (deadlineOnly) {
    return (
      <Styled.DeadlineContainer ref={containerRef}>
        <TimeLeft
          date={goal.deadline!}
          isShort
          startIcon={FlagFilled}
          isSmallIcon
        />

        {!!tooltip && (
          <Tooltip label={tooltip} referenceElement={containerRef} />
        )}
      </Styled.DeadlineContainer>
    );
  }

  return (
    <Styled.DeadlineContainer ref={containerRef}>
      {started && goal.deadline ? (
        <TimeLeft
          date={goal.deadline!}
          isShort
          startIcon={FlagFilled}
          isSmallIcon
        />
      ) : !!goal.startDate ? (
        <TimeLeft
          date={goal.startDate}
          isShort
          startIcon={Calendar10}
          noColor
          isSmallIcon
        />
      ) : null}

      {!!tooltip && <Tooltip label={tooltip} referenceElement={containerRef} />}
    </Styled.DeadlineContainer>
  );
};
